import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Tabs } from 'react-bootstrap';
import { Modal } from '@storable/canaveral-components';
import Button from '../../../../modules/react/components/stateless/Button';
import Field from '../../../../modules/react/components/stateful/Field';
import Form from '../../../../modules/react/components/stateful/Form';
import styles from './TabbedModal.scss';
import {
  isMediaGalleryOpen, toggleMediaGallery, getImages, uploadImages,
  getUsedMedia, setUsedMedium,
  getSelectedMedium, setSelectedMedia,
  getInitialImages, setInitialImages,
  clearMediaGallery, uploadImagesFromGallery,
  getSaveButtonClicked, saveButtonClicked,
} from '../../../../src/web/store/modules/mediaGallery';
import { detectVideoSource } from '../../../../src/web/utils/videoUtils';
import ImageGallerySelectorEditor from '../../../../src/web/components/sections/decorators/ImageGallerySelectorEditor';

class TabbedModal extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    initialValues: PropTypes.object,
    alwaysShowTabs: PropTypes.bool,
    wrapWithForm: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    children: PropTypes.node,
    onMount: PropTypes.func,
    onUnmount: PropTypes.func,
    onSelect: PropTypes.func,
    submitText: PropTypes.string,
    validate: PropTypes.func,
    onlyValidateOnSubmit: PropTypes.bool,
  };

  static defaultProps = {
    alwaysShowTabs: false,
    children: undefined,
    initialValues: {},
    onMount: undefined,
    onSelect: undefined,
    onUnmount: undefined,
    validate: undefined,
    wrapWithForm: false,
    submitText: 'Save',
    onlyValidateOnSubmit: undefined,
  };

  componentDidMount() {
    const { onMount } = this.props;

    if (onMount) {
      onMount.call(this);
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;

    if (onUnmount) {
      onUnmount.call(this);
    }
  }

  renderTabs() {
    const { children, alwaysShowTabs, onSelect, name, images } = this.props;

    if (React.Children.toArray(children).length === 1 && !alwaysShowTabs) {
      return children;
    }

    let defaultTab = 0;

    if (name === 'Call to Action: Custom-section-modal' && images.length > 0) {
      defaultTab = 3;
    }

    const validChildren = React.Children.toArray(children).filter(Boolean);

    return (
      <Tabs id="tabbed-modal" className={styles.tabs} defaultActiveKey={defaultTab} onSelect={onSelect}>
        {validChildren.map((element, index) => (
          React.isValidElement(element) ? React.cloneElement(element, { eventKey: index }) : null))}
      </Tabs>
    );
  }


  render() {
    // We pull out the other props here to prevent them from being sent down into the Modal
    /* eslint-disable no-unused-vars */
    const {
      title,
      name,
      initialValues,
      children,
      alwaysShowTabs,
      wrapWithForm,
      onSave,
      onCancel,
      onMount,
      onUnmount,
      submitText,
      validate,
      onlyValidateOnSubmit,
      isMediaGalleryVisible,
      mediaGalleryToggle,
      images,
      setImages,
      usedMedia,
      selectedMedium,
      setSelectedMedium,
      initialImages,
      galleryImageUpload,
      setExistingImages,
      clearMediaGalleryTempState,
      isSaveButtonClicked,
      setSaveButtonClicked,
      ...props
    } = this.props;
    /* eslint-enable no-unused-vars */
    const tabs = this.renderTabs();

    const saveHandler = (values) => {
      const updatedValues = { ...values };
      if (values?.sectionStyle && values?.sectionStyle?.backgroundStyle === 'none') {
        updatedValues.sectionStyle = { ...values.sectionStyle, backgroundColor: 'none', backgroundPattern: 'none' };
      }
      if (values?.sectionStyle && values?.sectionStyle?.overlayType === 'none') {
        updatedValues.sectionStyle = { ...values.sectionStyle, overlay: 'none' };
      }

      if (values?.sectionStyle && values?.sectionStyle?.backgroundStyle !== 'color') {
        updatedValues.sectionStyle = {
          ...updatedValues.sectionStyle,
          backgroundColor: 'none',
          backgroundPattern: 'none',
        };
      }

      if (values?.sectionStyle?.backgroundStyle === 'video' && !(values?.sectionStyle?.backgroundVideoType)) {
        updatedValues.sectionStyle = {
          ...updatedValues.sectionStyle,
          backgroundVideoType: 'youtubeVimeoEmbedded',
        };
      }

      if (updatedValues?.sectionStyle?.backgroundVideoType === 'youtubeVimeoEmbedded') {
        updatedValues.sectionStyle = {
          ...updatedValues.sectionStyle,
          backgroundVideoSource: detectVideoSource(
            updatedValues?.sectionStyle?.backgroundVideoURL || '',
          ),
          backgroundVideoEmbedCode: '',
        };
      }

      if (updatedValues?.sectionStyle?.backgroundStyle === 'image' && images.length > 0) {
        updatedValues.sectionStyle = {
          ...updatedValues.sectionStyle,
          images,
        };
      }

      if (isSaveButtonClicked) {
        onSave(updatedValues);
        clearMediaGalleryTempState();
      }
    };

    const saveButtonHandler = () => {
      setSaveButtonClicked();
    };

    const cancelHandler = () => {
      onCancel();
      clearMediaGalleryTempState();
    };

    const chooseImage = () => {
      galleryImageUpload(selectedMedium);
    };

    // todo: Respect `wrapWithForm`

    return (
      <Modal
        size="xl"
        show
        onDismiss={cancelHandler}
        aria-labelledby="tabbed-modal"
        {...props}
      >
        <Form
          form={name}
          onSubmit={saveHandler}
          initialValues={initialValues}
          validate={validate}
          onlyValidateOnSubmit={onlyValidateOnSubmit}
        >
          <Modal.Header closeButton>
            <Modal.Title id={title}>{isMediaGalleryVisible ? 'Media Gallery' : title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!isMediaGalleryVisible
              ? tabs
              : (
                <Field
                  component={ImageGallerySelectorEditor}
                  setShowGallery={mediaGalleryToggle}
                  usedMedia={usedMedia}
                  name="sectionStyle.images"
                  images={images}
                  setImages={setImages}
                  showGallery={isMediaGalleryVisible}
                />
              )
            }
          </Modal.Body>
          <Modal.Footer>
            {isMediaGalleryVisible
              ? (
                <>
                  <Button variant="default" onClick={mediaGalleryToggle}>Cancel</Button>
                  <Button
                    variant="success"
                    disabled={!selectedMedium}
                    onClick={chooseImage}
                  >Choose Image
                  </Button>
                </>
              ) : (
                <>
                  <Button data-testid="tabbed-modal-cancel" variant="default" onClick={cancelHandler}>Cancel</Button>
                  <Button data-testid="tabbed-modal-submit" type="submit" variant="success" onClick={saveButtonHandler}>{submitText}</Button>
                </>
              )}
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}

export default connect(
  state => ({
    isMediaGalleryVisible: isMediaGalleryOpen(state),
    images: getImages(state),
    usedMedia: getUsedMedia(state),
    selectedMedium: getSelectedMedium(state),
    initialImages: getInitialImages(state),
    isSaveButtonClicked: getSaveButtonClicked(state),
  }),
  {
    mediaGalleryToggle: toggleMediaGallery,
    setImages: uploadImages,
    setUsedMedia: setUsedMedium,
    setSelectedMedium: setSelectedMedia,
    setExistingImages: setInitialImages,
    clearMediaGalleryTempState: clearMediaGallery,
    galleryImageUpload: uploadImagesFromGallery,
    setSaveButtonClicked: saveButtonClicked,
  },
)(TabbedModal);
